import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Container,
  TextField,
  Typography,
  Pagination,
  Stack,
  styled,
  Grid,
  Autocomplete,
  Paper,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BsArrowRightCircle } from "react-icons/bs";
import { FaLocationDot } from "react-icons/fa6";
import homebanner from "@assets/images/Real Estate Banner-07.jpg";
import { useSearchParams } from "react-router-dom";
import { useFilter } from "@context/filterContext.jsx";
import SEO from "../../../utils/SeoComponent";
import { ROUTES } from "../../../constants/routes";
const DESCO_API_BASE = import.meta.env.VITE_DESCO_API_BASE;

// ========== STYLED COMPONENTS ========== //
const HeroSection = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "600px",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "150px",
    background: "linear-gradient(to top, #fff, transparent)",
  },
}));

const SearchBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 80,
  left: 0,
  right: 0,
  textAlign: "center",
  zIndex: 2,
  [theme.breakpoints.down("md")]: {
    bottom: 40,
  },
}));
const AreaCard = styled(Card)(({ theme }) => ({
  maxWidth: "100%",
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  transition: "transform 0.3s, box-shadow 0.3s",
  height: "100%",
  "&:hover": {
    transform: "translateY(-10px)",
    boxShadow: "0 16px 32px rgba(0,0,0,0.2)",
    "& .explore-text": {
      transform: "translateX(5px)",
    },
  },
}));

const CustomPaper = (props) => {
  return (
    <Paper
      {...props}
      sx={{
        borderRadius: "12px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
      }}
    />
  );
};

// ========== MAIN COMPONENT ========== //
const Area = () => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [titles, setTitles] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("query") || "";
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const itemsPerPage = 6;

  // Get filter context
  const {
    selectedLocation,
    setSelectedLocation,
    selectedRoom,
    setSelectedRoom,
    selectedPrice,
    setSelectedPrice,
    filteredUsers,
    setFilteredUsers,
    isFilterApplied,
    setIsFilterApplied,
  } = useFilter();

  // Fetch all areas on component mount
  useEffect(() => {
    const fetchAreas = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${DESCO_API_BASE}/areas/index`);

        if (response.data && response.data.data) {
          const areasData = response.data.data;
          setAreas(areasData);

          // Extract titles for autocomplete
          const titlesArray = areasData.map((item) => item.title);
          setTitles(titlesArray);
        }
      } catch (error) {
        console.error("Error fetching areas:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAreas();
  }, []);

  // Handle search/filter
  const handleSearch = async () => {
    if (!searchTerm && !selectedLocation && !selectedRoom && !selectedPrice) {
      // If no filters are applied, show all areas
      setIsFilterApplied(false);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();

      // Add search term if it exists
      if (searchTerm) {
        formData.append("title", searchTerm);
      }

      // Add other filters if they exist
      if (selectedLocation) {
        formData.append("location", selectedLocation);
      }

      if (selectedRoom) {
        formData.append("room", selectedRoom);
      }

      if (selectedPrice) {
        formData.append("price", selectedPrice);
      }

      const response = await axios.post(
          `${DESCO_API_BASE}/areas/filter`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data && response.data.data) {
        const filteredData = response.data.data;
        setFilteredUsers(filteredData);
        setIsFilterApplied(true);
        setCurrentPage(1); // Reset to first page when applying new filters
      }
    } catch (error) {
      console.error("Error fetching filtered data:", error);
      // In case of error, don't change filtered state
    } finally {
      setLoading(false);
    }
  };

  // Watch for search term changes and URL query changes
  useEffect(() => {
    if (searchQuery) {
      setSearchTerm(searchQuery);
    }
  }, [searchQuery]);

  // Apply search when search term changes
  useEffect(() => {
    // Use a debounce to avoid too many API calls
    const timer = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, selectedLocation, selectedRoom, selectedPrice]);

  // Determine which data set to use for display
  const displayData = isFilterApplied && filteredUsers ? filteredUsers : areas;

  // Calculate pagination
  const pageCount = Math.ceil((displayData?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAreas = Array.isArray(displayData)
    ? displayData.slice(startIndex, startIndex + itemsPerPage)
    : [];

  // Handle page change
  const handlePageChange = (_, newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const stripHTML = (html) => html.replace(/<\/?[^>]+(>|$)/g, "");

  return (
    <>
      <SEO slug={"areas"} />
      <Box sx={{ overflowX: "hidden" }}>
        {/* Hero Banner */}
        <HeroSection>
          <img src={homebanner} alt="Dubai Areas" />
          <SearchBox>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: 3,
                  textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                }}
              >
                Discover Dubai Areas
              </Typography>
              <Autocomplete
                options={titles}
                PaperComponent={CustomPaper}
                value={searchTerm}
                onInputChange={(event, newInputValue) => {
                  setSearchTerm(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Search areas..."
                    sx={{
                      width: { xs: "90%", sm: "70%", md: "50%" },
                      backgroundColor: "rgba(255,255,255,0.9)",
                      borderRadius: "50px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "50px",
                        "& fieldset": {
                          border: "none",
                        },
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <FaLocationDot
                          style={{ marginRight: 10, color: "#C10E14" }}
                        />
                      ),
                    }}
                  />
                )}
              />
            </motion.div>
          </SearchBox>
        </HeroSection>

        {/* Areas Grid */}
        <Container sx={{ py: 8 }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 6,
              textAlign: "center",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -10,
                left: "50%",
                transform: "translateX(-50%)",
                width: 80,
                height: 4,
                backgroundColor: "#C10E14",
              },
            }}
          >
            {isFilterApplied
              ? `Showing ${displayData?.length || 0} Results`
              : "Explore Popular Areas"}
          </Typography>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography>Loading areas...</Typography>
            </Box>
          ) : paginatedAreas.length > 0 ? (
            <Grid container spacing={4}>
              {paginatedAreas.map((area, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <AreaCard >
                      <CardActionArea
                        onClick={() =>
                          window.open(ROUTES.AREA_DETAIL(area?.slug), "_blank")
                        }
                        sx={{ height: 500, cursor: "pointer" }}
                      >
                        <Box sx={{ position: "relative" }}>
                          <CardMedia
                            component="img"
                            height="240"
                            image={
                              area.thumbnail ||
                              "https://via.placeholder.com/300x240?text=Area+Image"
                            }
                            alt={area.title}
                            sx={{
                              transition: "transform 0.5s",
                              "&:hover": {
                                transform: "scale(1.05)",
                              },
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: 16,
                              right: 16,
                              backgroundColor: "rgba(255,255,255,0.9)",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "16px",
                              fontSize: "12px",
                              fontWeight: "bold",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <FaLocationDot color="#C10E14" />
                            Dubai
                          </Box>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                          <Typography
                            gutterBottom
                            variant="h5"
                            component="h3"
                            sx={{
                              fontWeight: 700,
                              color: "#333",
                              mb: 2,
                              minHeight: "64px",
                            }}
                          >
                            {area.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2, lineHeight: 1.6 }}
                          >
                            {area?.description &&
                              (stripHTML(area.description).length > 100
                                ? `${stripHTML(area.description).slice(
                                    0,
                                    150
                                  )}...`
                                : stripHTML(area.description))}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              color: "#C10E14",
                              fontWeight: 600,
                              mt: 2,
                            }}
                          >
                            <span className="explore-text">Explore Area</span>
                            <BsArrowRightCircle
                              style={{
                                marginLeft: 8,
                                transition: "transform 0.3s",
                              }}
                            />
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </AreaCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: "center", py: {sm:1,md:8}}}>
              <Typography variant="h6">
                No areas found matching your search criteria.
              </Typography>
            </Box>
          )}

          {/* Pagination */}
          {pageCount > 1 && (
            <Stack spacing={2} sx={{ mt: 6, alignItems: "center" }}>
              <Pagination
              count={pageCount}
                page={currentPage}
                onChange={handlePageChange}
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontWeight: 600,
                    "&.Mui-selected": {
                      backgroundColor: "#C10E14",
                      color: "white",
                    },
                  },
                }}
              />
            </Stack>
          )}
        </Container>
      </Box>
    </>
  );
};

export default Area;
